export const CV_META = {
  title: "CJ Jayamaha's CV",
  description:
    "I am a Software Product Specialist with 20+ years in software — B2B, B2C, and B2B2C products across SaaS, AI, fintech, insurtech, and more. HSBC, Oakam, Scout Energy, RubyLife, and beyond.",
  path: "/CJJayamahas-CV",
  ogImage: "og/cj-cv-share.jpg",
  ogImageAlt:
    "CJ Jayamaha — Software Product Specialist, professional portrait and CV preview",
  twitterImage: "og/cj-cv-share-twitter.jpg",
  ogImageWidth: 1200,
  ogImageHeight: 630,
  twitterImageWidth: 1200,
  twitterImageHeight: 600,
  imageType: "image/jpeg",
};

export const CV_PROFILE = {
  name: "CJ Jayamaha",
  tagline:
    "Software product specialist in solutions & transformation across industries, balancing strategy and execution to turn complex ideas into simple, scalable products. Experience spans global brands and startups.",
  locations: "London, United Kingdom / Faro, Portugal",
  nationality: "British / EU",
  email: "jayamaha.cj@gmail.com",
  phoneUk: "+44 7545 994 556",
  phonePt: "+351 926 214 888",
  linkedin: "https://www.linkedin.com/in/cjmeister/",
};

export const CV_INTRO = {
  title: "Introduction",
  videoId: "mYSS1UjxvT4",
  videoCaption: "CJ Jayamaha - in his own words",
  paragraphs: [
    "I am a Software Product Specialist with 20+ years in software. I started in software engineering and technical architecture before moving into end-to-end product leadership.",
    "My approach brings together three perspectives: technical depth, a design-led grounding in usability and customer experience, and the commercial judgment I have developed through an entrepreneurial career.",
    "I have built and scaled B2B, B2C and B2B2C products across SaaS, AI, fintech, insurtech, adtech, gaming, renewable energy, retail and e-commerce. I take products from discovery and validation through strategy, prioritisation, delivery, launch and optimisation, with a consistent focus on simplifying complex systems and creating measurable value for customers and the business.",
  ],
};

export const CV_ACHIEVEMENTS = [
  {
    id: "hsbc",
    headline: "HSBC TED",
    metric: "$334M",
    detail:
      "Envisioned and deployed the Touch Enabled Dashboard across web and mobile — $202M cost savings and $132M new revenue.",
    link: {
      label: "Early promo video",
      href: "https://vimeo.com/216460322/d352e3c6f0",
    },
  },
  {
    id: "nokia",
    headline: "Nokia / Microsoft",
    metric: "60+",
    detail:
      "Led development and rollout of a global e-commerce marketplace unifying online sales across more than 60 countries.",
  },
  {
    id: "oakam",
    headline: "Oakam",
    metric: "£30M",
    detail:
      "Transformed a retail lender into a mobile-first fintech, generating £30M in new lending revenue.",
  },
  {
    id: "rubylife",
    headline: "RubyLife",
    metric: "$189M",
    detail:
      "Launched a cross-platform social-dating app in 7 months, scaling to $189M revenue (2022).",
  },
  {
    id: "ewc",
    headline: "Esports World Cup",
    metric: "750M+ users",
    detail:
      "Delivered web and native mobile platforms for ESL FACEIT Group at esportsworldcup.com.",
    link: {
      label: "esportsworldcup.com",
      href: "https://esportsworldcup.com",
    },
  },
  {
    id: "mindfoundry",
    headline: "MindFoundry × AND-E",
    metric: "Real-time",
    detail:
      "Launched an AI-driven insurance pricing engine with Toyota — dynamic, usage-based premiums in Italy.",
  },
];

export const CV_SKILLS = [
  "Product Discovery",
  "Product Strategy",
  "Roadmapping",
  "Agile Delivery",
  "UX & CX Strategy",
  "Data-Driven Decision Making",
  "Experimentation",
  "Go-to-Market Planning",
  "Stakeholder Management",
  "MVP Execution",
  "Revenue Generation",
];

/** @typedef {{ label: string, href: string, embed?: "loom" | "vimeo" | "youtube" }} CvRoleLink */
/** @typedef {{ company: string, role: string, location: string, period: string, arrangement: string, summary?: string, bullets?: string[], link?: CvRoleLink }} CvRole */
export const CV_EXPERIENCE = [
  {
    company: "Scout Energy",
    role: "Founder & Product Director",
    location: "UK",
    period: "Jul 2025 – Aug 2026",
    arrangement: "Hybrid-Remote, Permanent",
    summary:
      "Led product development for Scout Energy, a data and intelligence platform helping data centre developers and energy infrastructure investors identify and evaluate optimal development sites. Consolidated fragmented datasets on electricity grid capacity, renewable energy availability, connectivity, permitting status, and land ownership to support site selection and investment decisions.",
    bullets: [
      "Defined the product vision, platform architecture, and multi-phase roadmap for a global infrastructure marketplace connecting data centre developers, renewable energy providers, infrastructure investors, and consultants.",
      "Designed the platform's core data architecture integrating electricity grid topology, renewable generation proximity, fibre connectivity, regulatory signals, environmental indicators, infrastructure access, and land ownership datasets into a unified intelligence platform.",
      "Led product discovery, market validation, and MVP definition, establishing a scalable data ingestion and normalisation architecture capable of aggregating fragmented international infrastructure datasets.",
      "Designed and implemented an ElasticSearch-driven data discovery layer enabling complex multi-parameter search, filtering, and comparison of infrastructure opportunities across multiple geographies.",
      "Developed AI-driven capabilities including automated data aggregation, intelligent structuring of fragmented datasets, conversational search and requirement matching, automated risk signal detection, and predictive modelling of energy demand and grid capacity pressure.",
      "Directed cross-functional collaboration across engineering and data teams to deliver the platform's scalable cloud infrastructure, high-volume data processing pipelines, and intelligent query capabilities.",
      "Developed the commercial model positioning Scout Energy as a global infrastructure marketplace enabling discovery, comparison, and due diligence of energy-intensive development opportunities.",
      "Built the strategic narrative, investor materials, and product demonstrations that supported growth and ultimately led to the company's successful acquisition.",
    ],
    link: {
      label: "Platform overview video",
      href: "https://www.youtube.com/watch?v=u-6P9F-UpDI",
      embed: "youtube",
    },
  },
  {
    company: "MindFoundry.ai",
    role: "Product Consultant",
    location: "Oxford, UK",
    period: "Mar 2025 – Jun 2025",
    arrangement: "Contract",
    summary:
      "Delivered an AI-driven, real-time vehicle insurance pricing engine for AND-E Italy in partnership with Toyota, enabling dynamic pay-for-what-you-use premiums across new and used vehicles.",
    bullets: [
      "Defined and launched a customisable pricing engine integrating personal, vehicle, and third-party data processed through advanced AI models.",
      "Translated complex data-science and AI outputs into clear, user-friendly workflows for underwriters and pricing analysts.",
      "Designed geolocation-based risk-zone models using behavioural and environmental data, supporting human overrides for regulatory transparency.",
      "Collaborated with Toyota, dealerships, and software vendors to align product capabilities with market and compliance needs.",
      "Simplified highly technical systems into intuitive interfaces, empowering business teams to adjust pricing logic without engineering support.",
    ],
  },
  {
    company: "EFG (ESL FACEIT Group)",
    role: "Product Manager",
    location: "London, UK",
    period: "Mar 2024 – Mar 2025",
    arrangement: "Contract",
    summary:
      "Global eGaming platform for over 800M+ users worldwide. Led product management for the global relaunch of ESL FACEIT Group's esports web and mobile ecosystem, including the flagship Esports World Cup (EWC) 2025 platform. The initiative unified multiple legacy systems into a scalable, data-driven experience supporting millions of competitive-gaming fans worldwide.",
    bullets: [
      "Directed the vision, roadmap, and UX strategy for the EWC website and mobile app, delivering to aggressive pre-tournament timelines.",
      "Oversaw end-to-end product delivery from discovery through launch, aligning design, engineering, and marketing teams across global regions.",
      "Achieved record engagement metrics — 5.4M+ monthly active users and 41.7M total visits in 2024 — by optimising content architecture and user flows.",
      "Introduced KPI-based backlog prioritisation to balance fan engagement, monetisation, and partner integration goals.",
      "Championed an iterative, cross-functional approach that improved release cadence, design quality, and stakeholder confidence.",
    ],
    link: {
      label: "EWC platform overview",
      href: "https://www.youtube.com/watch?v=o1WgDPLGd3E",
      embed: "youtube",
    },
  },
  {
    company: "FlyFish Limited",
    role: "Head of Product",
    location: "Cyprus",
    period: "Jan 2023 – Mar 2024",
    arrangement: "Remote, Contract",
    summary:
      "Defined and delivered the product vision, strategy, and roadmap for FlyFish — a subscription-based SaaS platform providing global payments, IBAN issuance, business debit cards, and crypto support on a single ecosystem.",
    bullets: [
      "Defined the business and consumer propositions, aligning all teams and stakeholders around a unified value narrative.",
      "Built and executed the product roadmap covering payments, payroll, and compliance modules.",
      "Partnered with the CEO and investors to shape the business model, pricing, and go-to-market plan.",
      "Led cross-functional teams across engineering, design, and QA to deliver incremental, high-impact releases.",
      "Integrated third-party platforms including Wallester and Yoti, accelerating delivery and compliance readiness.",
      "Ensured all features were built with scalability, security, and regulatory compliance from the outset.",
    ],
    link: {
      label: "Proposition demo",
      href: "https://www.loom.com/share/5a073d46a0854219a7a67b9a55a3ca38",
      embed: "loom",
    },
  },
  {
    company: "RubyLife Limited",
    role: "Lead Product Manager",
    location: "Canada",
    period: "Sep 2021 – Jan 2023",
    arrangement: "Hybrid",
    summary:
      "Hired to launch a new cross-platform mobile app as an upgrade to RubyLife's existing application. Led a cross-functional team of architects, designers, developers, and testers to deliver a new application which exceeded $189M USD in revenue.",
    bullets: [
      "Created a prioritised product feature map by evaluating feature ROI — feature-level analytics, revenue per feature, and cost to deliver calculated to agree priority (MoSCoW methodology).",
      "Led the technical teams to create a new tech strategy to vastly reduce technical debt via a micro-service-led architecture, new database structures, and data transfer scripts from old to new.",
      "Led the UI and UX teams to redesign all web, mobile web, and native journeys along with click-through prototypes.",
      "Led in-house and live testing with a select user group to ensure the new system works perfectly.",
      "Delivered company-wide demonstrations of the new application to inform and educate operational staff.",
      "Released the application on time and within budget.",
    ],
  },
  {
    company: "Future Finance Limited",
    role: "Lead Product Manager",
    location: "London, UK",
    period: "Sep 2022 – Jan 2023",
    arrangement: "Hybrid, Contract",
    summary:
      "Led a company-wide product transformation programme to drive profitability for this student-lending fintech, reporting directly to the CEO.",
    bullets: [
      "Developed and executed a 24-month product strategy addressing key blockers in onboarding, underwriting, operations, and collections.",
      "Identified £800K in annual cost savings through automation and process optimisation within underwriting and onboarding.",
      "Defined and delivered a six-month roadmap of initiatives tied to cost reduction and revenue growth (£28M GBP).",
      "Partnered with engineering, design, and operations to implement tools improving efficiency and user experience across web and mobile channels.",
      "Positioned the company on a clear path to profitability, aligning tactical delivery with long-term business goals.",
    ],
  },
  {
    company: "Tesco Plc",
    role: "Product Manager",
    location: "UK",
    period: "Feb 2021 – Sep 2021",
    arrangement: "Remote, Contract",
    summary:
      "Supported Tesco Online Grocery business growth toward £10bn per annum by delivering key digital propositions that enable automated, semi-automated, and manual order fulfilment — picking of same-day and click-and-collect orders, delivery orchestration — across fulfilment centres and stores.",
    bullets: [
      "Strategised, planned, and delivered new fulfilment capabilities whilst improving existing features.",
      "Partnered with a cross-functional team of UX/UI designers, engineers, service product managers, business sponsors, and other stakeholders.",
      "Delivered digital propositions spanning automated, semi-automated, and manual picking workflows across fulfilment centres and stores.",
      "Orchestrated delivery orchestration capabilities for same-day and click-and-collect grocery orders.",
    ],
  },
  {
    company: "Oakam Ltd",
    role: "Lead Product Manager",
    location: "London, UK",
    period: "Nov 2018 – Dec 2021",
    arrangement: "Hybrid, Employment",
    summary:
      "Led the transformation of Oakam from a retail lender into a mobile-first fintech, modernising its technology, product strategy, and customer experience.",
    bullets: [
      "Defined and delivered a new digital lending ecosystem, launching a mobile app and decision engine that generated £30M in new lending revenue.",
      "Secured FCA approval for the company's digital product strategy by aligning product delivery with regulatory standards.",
      "Conducted gap-analysis workshops across product, UX, compliance, and technology to build a roadmap for modernisation.",
      "Collaborated with designers, architects, and engineers to implement document capture, face recognition, and behavioural data models to improve underwriting and onboarding.",
      "Reduced fraud by 95% and first-default rate by 73% through data-driven journey redesigns.",
      "Built and managed distributed onshore/offshore teams, improving delivery speed, capability, and cost efficiency.",
    ],
  },
  {
    company: "BT Group plc (EE)",
    role: "E-Commerce Product Manager / Owner",
    location: "UK",
    period: "Jan 2018 – Jan 2019",
    arrangement: "Onsite, Contract",
    summary:
      "EE is BT Group's flagship consumer brand and the second-largest mobile network operator in the UK with 21.2 million customers. Delivered a new e-commerce platform overcoming key challenges in the existing system — single customer view, single basket for the purchase funnel, and more.",
    bullets: [
      "Led two key regulation-led projects where complex business and regulatory requirements were delivered to users as simple, self-explanatory solutions.",
      "Delivered tangible benefits to consumers and the business by creating consensus among numerous stakeholders.",
      "Defined and shipped a unified e-commerce platform replacing fragmented legacy purchase flows.",
      "Drove single customer view and single-basket capabilities across the purchase funnel.",
    ],
  },
  {
    company: "Experian Ltd",
    role: "Head of Product, Global Expansion",
    location: "UK",
    period: "Aug 2017 – Nov 2018",
    arrangement: "Onsite, Permanent",
    summary:
      "Experian is a multinational data analytics and consumer credit reporting company. Created a global expansion product strategy to take UK-centric Experian consumer propositions to developing markets such as India, South Africa, and Colombia.",
  },
  {
    company: "HSBC Global RBWM",
    role: "Senior Digital Product Manager",
    location: "UK",
    period: "Nov 2015 – Aug 2017",
    arrangement: "Onsite, Contract",
    summary:
      "Created a global digital proposition to transform how HSBC branches interact with retail and wealth management customers and staff, as part of a $1.7bn USD transformation programme. TED (Touch Enabled Dashboard) demonstrated cost savings of $202M+ USD and revenue increase of $132M+ USD — planned for deployment across all HSBC countries, supporting hundreds of customer and staff-centred journeys.",
    bullets: [
      "Created the TED proposition strategy and vision, employing user research and observation tactics to identify opportunities and challenges.",
      "Introduced the very first native tablet application to HSBC RBWM.",
      "Built a ground-up team of UX designers, UI designers, technical architects, business analysts, software engineers, and QA engineers — establishing on-shore, off-shore, and in-market delivery plans.",
      "Planned and prioritised the backlog to define MVP features.",
      "Led user workshops across multiple markets; results informed product strategy at feature level.",
      "Collaborated with enterprise solution architects and senior developers to design the technical solution.",
      "Provided strategic input to C-level stakeholders (CEO for Group RBWM, Group COO, etc.) by demonstrating product capability and least-painful transition paths to success.",
      "Worked with the global process optimisation team to identify process inefficiencies across products, sales, and servicing.",
      "Introduced user-centric solution design while avoiding 'a faster horse' syndrome.",
      "Engaged marketing and design governance teams to deliver innovative user journey modifications — changing some of HSBC's design principles.",
      "Worked with Risk, Legal & Compliance, Global & Local IT, Global Standards, and Digital Risk Groups to ensure compliant application journeys.",
      "Identified underperforming solutions through workshops with Global Wealth and Retail banking owners and created strategies to improve ROI.",
      "Provided consultancy to other global programmes to create best-in-class applications.",
      "Created a product vision and roadmap delivering consistent incremental value where cultural, technological, and procedural limitations hindered complete business transformation.",
    ],
    link: {
      label: "TED promo video",
      href: "https://vimeo.com/216460322/d352e3c6f0",
      embed: "vimeo",
    },
  },
];

export const CV_EDUCATION = {
  institution: "University of Westminster, London",
  qualification: "B.Sc. (Hons) Software Engineering",
  grade: "Upper Second-Class (2:1)",
  certification: "APMG International Certified Agile Practitioner",
};
